import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Typography,
    Box,
    TextField,
    Button,
    CircularProgress,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Switch,
    FormControlLabel,
} from '@mui/material';
import { Close, Save } from '@mui/icons-material';
import { toast } from 'react-toastify';

const GlobalAgentConfigDrawer = ({ open, onClose, agent, onSave, onSuccess }) => {
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        agentName: '',
        agentAppName: '',
        agentToken: '',
        description: '',
        version: '',
        owner: '',
        category: '',
        context: '',
        systemPrompt: '',
        knowledgeScope: '',
        agentConfig: '',
        baseUrl: '',
        webhookEndpoint: '',
        apiKey: '',
        isActive: true,
    });
    const [errors, setErrors] = useState({});

    const isEditMode = !!agent;

    useEffect(() => {
        if (open) {
            if (agent) {
                // Populate form with agent data
                setFormData({
                    agentName: agent.agentName || '',
                    agentAppName: agent.agentAppName || '',
                    agentToken: agent.agentToken || '',
                    description: agent.description || '',
                    version: agent.version || '',
                    owner: agent.owner || '',
                    category: agent.category || '',
                    context: agent.context || '',
                    systemPrompt: agent.systemPrompt || '',
                    knowledgeScope: agent.knowledgeScope || '',
                    agentConfig: typeof agent.agentConfig === 'string'
                        ? agent.agentConfig
                        : JSON.stringify(agent.agentConfig, null, 2),
                    baseUrl: agent.baseUrl || '',
                    webhookEndpoint: agent.webhookEndpoint || '',
                    apiKey: agent.apiKey || '',
                    isActive: agent.isActive !== undefined ? agent.isActive : true,
                });
            } else {
                // Reset form for new agent
                setFormData({
                    agentName: '',
                    agentAppName: '',
                    agentToken: '',
                    description: '',
                    version: '',
                    owner: '',
                    category: '',
                    context: '',
                    systemPrompt: '',
                    knowledgeScope: '',
                    agentConfig: '',
                    baseUrl: '',
                    webhookEndpoint: '',
                    apiKey: '',
                    isActive: true,
                });
            }
            setErrors({});
        }
    }, [open, agent]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.agentName || formData.agentName.trim().length < 3) {
            newErrors.agentName = 'Agent name is required and must be at least 3 characters';
        }

        if (!formData.baseUrl) {
            newErrors.baseUrl = 'Base URL is required';
        }

        if (!formData.webhookEndpoint) {
            newErrors.webhookEndpoint = 'Webhook endpoint is required';
        }

        // apiKey is only required for n8n agents.
        // Langchain agents do not require apiKey for ChatHub routing.
        const requiresApiKey = !formData.agentAppName || String(formData.agentAppName).toLowerCase() !== 'langchain';
        if (requiresApiKey && !formData.apiKey) {
            newErrors.apiKey = 'API key is required';
        }

        // Validate JSON if agentConfig is provided
        if (formData.agentConfig) {
            try {
                JSON.parse(formData.agentConfig);
            } catch (e) {
                newErrors.agentConfig = 'Invalid JSON format';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            toast.error('Please fix the errors in the form');
            return;
        }

        setSaving(true);
        try {
            const dataToSave = {
                ...formData,
                agentConfig: formData.agentConfig ? JSON.parse(formData.agentConfig) : null,
            };

            await onSave(dataToSave);
            if (onSuccess) {
                await onSuccess();
            }
            onClose();
        } catch (error) {
            console.error('Error saving agent:', error);
            toast.error(`Error saving agent: ${error.message}`);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 2 }
            }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider', py: 2 }}>
                <Typography variant="h6">
                    {isEditMode ? 'Edit Global Agent' : 'Create Global Agent'}
                </Typography>
                <IconButton onClick={onClose} size="small" aria-label="close">
                    <Close />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
                <Box>
                    <Grid container spacing={2}>
                        {/* Agent Information Section */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#1976d2' }}>
                                Agent Information
                            </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Agent Name *"
                                name="agentName"
                                value={formData.agentName}
                                onChange={handleInputChange}
                                error={!!errors.agentName}
                                helperText={errors.agentName}
                                size="small"
                                required
                            />
                        </Grid>



                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Agent App Name</InputLabel>
                                <Select
                                    name="agentAppName"
                                    value={formData.agentAppName}
                                    onChange={handleInputChange}
                                    label="Agent App Name"
                                >
                                    <MenuItem value="">-- Select --</MenuItem>
                                    <MenuItem value="n8n">n8n</MenuItem>
                                    <MenuItem value="langchain">langchain</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Category"
                                name="category"
                                value={formData.category}
                                onChange={handleInputChange}
                                size="small"
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Description"
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                multiline
                                rows={3}
                                size="small"
                            />
                        </Grid>

                        {/* Webhook Configuration Section (CDX Only) */}
                        <Grid item xs={12}>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: '#1976d2' }}>
                                Webhook Configuration (Admin Only)
                            </Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Base URL *"
                                name="baseUrl"
                                value={formData.baseUrl}
                                onChange={handleInputChange}
                                error={!!errors.baseUrl}
                                helperText={errors.baseUrl || 'e.g., https://synctrack.asrotrack.com'}
                                size="small"
                                required
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Webhook Endpoint *"
                                name="webhookEndpoint"
                                value={formData.webhookEndpoint}
                                onChange={handleInputChange}
                                error={!!errors.webhookEndpoint}
                                helperText={errors.webhookEndpoint || 'e.g., /n8n/production/abc123/chat'}
                                size="small"
                                required
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="API Key *"
                                name="apiKey"
                                value={formData.apiKey}
                                onChange={handleInputChange}
                                error={!!errors.apiKey}
                                helperText={errors.apiKey}
                                size="small"
                                type="password"
                                required
                            />
                        </Grid>

                        {/* System Prompt Section */}
                        <Grid item xs={12}>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                                Agent Persona
                            </Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="System Prompt"
                                name="systemPrompt"
                                value={formData.systemPrompt}
                                onChange={handleInputChange}
                                multiline
                                rows={4}
                                size="small"
                                helperText="Define the agent's personality and behavior"
                            />
                        </Grid>

                        {/* Agent Config Section */}
                        <Grid item xs={12}>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                                Advanced Configuration (JSON)
                            </Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Agent Config"
                                name="agentConfig"
                                value={formData.agentConfig}
                                onChange={handleInputChange}
                                multiline
                                rows={8}
                                size="small"
                                error={!!errors.agentConfig}
                                helperText={errors.agentConfig || 'Enter JSON configuration'}
                                sx={{
                                    '& .MuiInputBase-input': {
                                        fontFamily: 'monospace',
                                        fontSize: '0.875rem'
                                    }
                                }}
                            />
                        </Grid>

                        {/* Active Status */}
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.isActive}
                                        onChange={handleInputChange}
                                        name="isActive"
                                    />
                                }
                                label="Active"
                            />
                        </Grid>
                    </Grid>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider', gap: 1 }}>
                <Button onClick={onClose} variant="outlined">
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={16} /> : <Save />}
                    disabled={saving}
                >
                    {saving ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default GlobalAgentConfigDrawer;
